import { mountChart } from "../../charts/index.js";
import { pnlSteps } from "../../charts/model.js";
import { API } from "../../api.js";
import { $, fmtMoney } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";

/**
 * Financials: statements and ratios in one section, kept simple.
 *
 *   Summary         four plain health checks and the trend
 *   Profit & loss   where the month's revenue went (waterfall + the lines)
 *   Balance sheet   what you own against what you owe and the owners' share
 *   Cash flow       start cash, what came in and went out, end cash
 *   Ratios          each ratio with what it means and what healthy looks like
 *   Living Ledger   the month's journal as money moving between jars
 *
 * renderRatios (the old Ratios tab) opens this section on Ratios.
 */
let financialsSubTab = "summary";
let pnlChart = null, trendChart = null;

const FIN_TABS = [["summary", "Summary"], ["pnl", "Profit & loss"], ["balance", "Balance sheet"], ["cashflow", "Cash flow"], ["ratios", "Ratios"]];
const fm = (v) => fmtMoney(v);
const fmc = (v) => {
  const a = Math.abs(v || 0), s = v < 0 ? "-" : "";
  return a >= 1e9 ? `${s}$${(a / 1e9).toFixed(1)}B` : a >= 1e6 ? `${s}$${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${s}$${(a / 1e3).toFixed(0)}K` : fmtMoney(v);
};
const pctTxt = (v, d = 1) => (v === null || v === undefined || !Number.isFinite(+v) ? "—" : `${(+v).toFixed(d)}%`);
const xTxt = (v) => (v === null || v === undefined || !Number.isFinite(+v) ? "—" : `${(+v).toFixed(2)}×`);

/** Cash right now (the top bar's number), not the month-close figure: they differ after a loan or a sale mid-month. */
function finCashNow(bs) {
  return window.__live && typeof window.__live.cash === "number" ? window.__live.cash : bs.CASH;
}

function finRunway(is, bs) {
  const burn = Math.max(0, -is.net_income);
  return burn > 0 ? finCashNow(bs) / burn : null;
}

/** "0%" for a −0.4% margin read as a bug: small margins keep one decimal. */
const finMarginTxt = (v) => pctTxt(v, Math.abs(+v || 0) < 1 ? 1 : 0);

function finPlain(is, bs) {
  const run = finRunway(is, bs), cash = finCashNow(bs);
  // a brand-new company: nothing sold, nothing spent yet (it used to say "You're profitable, so cash is building")
  if (!is.revenue && !is.net_income) {
    return `Nothing sold yet: revenue starts when you launch your first product (Products). You have <b>${fmc(cash)}</b> in cash to build with.`;
  }
  return `${is.net_income >= 0 ? `You made <b>${fm(is.net_income)}</b> profit this month` : `You lost <b>${fm(-is.net_income)}</b> this month`}
    on <b>${fm(is.revenue)}</b> of revenue. ${run === null ? "You're profitable, so cash is building."
      : run >= 12 ? `Your ${fmc(cash)} cash would last over a year at this rate.`
      : `At this rate your ${fmc(cash)} cash lasts about <b>${run.toFixed(1)} months</b>${run < 4 ? " ⚠" : ""}.`}`;
}

/** Four plain checks. */
function finHealth(snap, history) {
  const is = snap.income_statement, bs = snap.balance_sheet, r = snap.ratios || {};
  const prev = history.length > 1 ? history[history.length - 2].income_statement : null;
  const growth = prev && prev.revenue ? (100 * (is.revenue - prev.revenue)) / prev.revenue : null;
  const run = finRunway(is, bs);
  const card = (q, ok, value, line, sub) => `<div class="fin-health ${ok}"><span class="fin-q">${q}</span><b class="fin-big">${value}</b><p>${line}</p>${sub ? `<em>${sub}</em>` : ""}</div>`;
  return `<div class="fin-healths">
    ${card("Are you making money?", r.net_margin_pct > 8 ? "good" : r.net_margin_pct >= 0 ? "ok" : "bad", pctTxt(r.net_margin_pct),
      r.net_margin_pct >= 0 ? `You keep ${finMarginTxt(r.net_margin_pct)} of every dollar of revenue.` : `You lose ${finMarginTxt(-r.net_margin_pct)} on every dollar of revenue.`, "net margin · healthy above 8%")}
    ${card("Can you pay the bills?", run === null || run >= 12 ? "good" : run >= 4 ? "ok" : "bad", run === null ? (is.revenue ? "Building" : "—") : `${run.toFixed(1)} mo`,
      run === null ? (is.revenue ? "Profitable: cash grows every month." : "Nothing spent or sold yet.") : `Cash runway at this month's burn. Short-term bills are covered ${xTxt(r.current_ratio)}.`, "runway · healthy above 12 months")}
    ${card("Is the debt safe?", r.debt_to_equity === null || r.debt_to_equity < 1 ? "good" : r.debt_to_equity < 2 ? "ok" : "bad", r.credit_rating || "—",
      `Debt is ${xTxt(r.debt_to_equity)} your equity; profit covers interest ${xTxt(r.interest_coverage)}.`, "credit rating · debt/equity healthy below 1×")}
    ${card("Are you growing?", growth === null ? "ok" : growth > 2 ? "good" : growth >= 0 ? "ok" : "bad", growth === null ? "—" : `${growth >= 0 ? "+" : ""}${growth.toFixed(1)}%`,
      growth === null ? "One more month to compare." : `Revenue ${growth >= 0 ? "grew" : "fell"} versus last month.`, "monthly revenue change · healthy above 2%")}
  </div>`;
}

function finPnlHtml(is) {
  const rev = is.revenue || 0;
  const row = (label, v, kind) => {
    const pct = rev ? Math.min(100, (100 * Math.abs(v)) / rev) : 0;
    return `<tr class="${kind || ""}"><td class="label">${label}</td>
      <td class="fin-barcell"><i class="fin-bar ${v < 0 || kind === "cost" ? "cost" : ""}" style="width:${pct.toFixed(1)}%"></i></td>
      <td class="mono">${kind === "cost" ? `(${fm(v)})` : fm(v)}</td><td class="mono fin-pct">${rev ? `${((100 * v) / rev).toFixed(0)}%` : ""}</td></tr>`;
  };
  return `<div class="chart-wrap" style="height:260px;"><div id="pnl-waterfall" class="ck-host"></div></div>
    <table class="ledger fin-table">
      ${row("Revenue", is.revenue)}
      ${row("Cost of making what you sell", is.cogs, "cost")}
      ${row("Gross profit", is.gross_profit, "total")}
      ${row("Research & development", is.rnd, "cost")}
      ${row("Sales & marketing", is.snm, "cost")}
      ${row("Staff & running the company", (is.gna || 0) - (is.restructuring || 0), "cost")}
      ${is.restructuring ? row("Restructuring (one-off: severance)", is.restructuring, "cost") : ""}
      ${row("Operating profit (before depreciation)", is.ebitda, "total")}
      ${row("Equipment wearing out", is.depreciation, "cost")}
      ${row("Interest on loans", is.interest, "cost")}
      ${is.investment_income ? row("Investment income (dividends, gains or losses)", is.investment_income) : ""}
      ${row("Tax", is.tax, "cost")}
      ${row(is.net_income >= 0 ? "Profit you keep" : "Loss", is.net_income, "total net")}
    </table><p class="chart-caption">Tap a line to see the journal entries behind it in the Living Ledger.</p>`;
}

function finBalanceHtml(snap) {
  const bs = snap.balance_sheet, ni = (snap.income_statement && snap.income_statement.net_income) || 0;
  const own = [["CASH", "Cash in the bank"], ["AR", "Owed to you by customers"], ["INVENTORY", "Stock"], ["PREPAID", "Paid in advance"],
    ["PPE", "Equipment & sites"], ["INTANGIBLES", "Product development & IP"], ["INVESTMENTS", "Investments"]];
  const owe = [["AP", "Owed to suppliers"], ["ACCRUED", "Bills & tax due"], ["DEFERRED_REV", "Customers' prepayments"], ["ST_DEBT", "Short-term loans"], ["LT_DEBT", "Long-term loans"]];
  // owners' share, split so this month's profit is visible: retained earnings at a close already include it
  const eqVals = { SHARE_CAPITAL: bs.SHARE_CAPITAL || 0, RE_BEFORE: (bs.RETAINED_EARNINGS || 0) - ni, RE_MONTH: ni };
  const eq = [["SHARE_CAPITAL", "Money the owners put in"], ["RE_BEFORE", "Profits kept from earlier months (or losses)"], ["RE_MONTH", `Month ${snap.month}'s profit (or loss)`]];
  const val = (k) => (k in eqVals ? eqVals[k] : bs[k] || 0);
  const max = Math.max(1, ...[...own, ...owe, ...eq].map(([k]) => Math.abs(val(k))));
  const list = (items, color) => items.filter(([k]) => Math.abs(val(k)) >= 0.5).map(([k, l]) => `
    <div class="fin-line"><span>${l}</span><i class="fin-hbar" style="width:${(100 * Math.abs(val(k)) / max).toFixed(1)}%;background:${val(k) < 0 ? "var(--oxblood)" : color}"></i><b class="${val(k) < 0 ? "neg" : ""}">${fm(val(k))}</b></div>`).join("")
    || `<p class="fin-none">Nothing here.</p>`;
  const total = (label, v) => `<div class="fin-line fin-total"><span>${label}</span><i></i><b class="${v < 0 ? "neg" : ""}">${fm(v)}</b></div>`;
  const ok = Math.abs(bs.total_assets - (bs.total_liabilities + bs.total_equity)) < 1;
  const since = window.__live ? window.__live.cash_change_since_close : 0;
  const sinceNote = since && Math.abs(since) >= 0.5
    ? `<p class="fin-since">ⓘ This is the balance sheet at the close of month ${snap.month}. Since then cash has ${since < 0 ? "gone down" : "gone up"} by ${fm(Math.abs(since))} (spending and actions this month), so the top bar shows ${fm(window.__live.cash)} cash right now. Next month's close brings everything up to date.</p>` : "";
  return `<div class="fin-bs-head">Balance sheet at the close of month ${snap.month}</div>
    <div class="fin-eqline ${ok ? "ok" : "bad"}"><span><em>What you own</em><b>${fm(bs.total_assets)}</b></span><i>${ok ? "=" : "≠"}</i>
      <span><em>What you owe</em><b>${fm(bs.total_liabilities)}</b></span><i>+</i><span><em>Owners' share</em><b class="${bs.total_equity < 0 ? "neg" : ""}">${fm(bs.total_equity)}</b></span>${ok ? `<i class="fin-ok">✓ balanced</i>` : ""}</div>
    ${sinceNote}
    <div class="fin-bs">
      <section><h4>What you own</h4>${list(own, "var(--brass)")}${total("Total you own", bs.total_assets)}</section>
      <div class="fin-eq">${ok ? "=" : "≠"}</div>
      <section><h4>What you owe</h4>${list(owe, "#f6b26b")}${total("Total you owe", bs.total_liabilities)}
        <h4 class="fin-h2">+ Owners' share</h4>${list(eq, "var(--neon-violet)")}${total("Total owners' share", bs.total_equity)}</section>
    </div><p class="chart-caption">${ok ? "Balanced: everything you own was paid for by someone, either lenders and suppliers or the owners." : "Out of balance: this shouldn't happen."}</p>`;
}

function finCashHtml(cf) {
  const steps = [["Cash at the start", cf.cash_beginning, "start"], ["From running the business", cf.operating_cf, "flow"], ["Invested (equipment, acquisitions)", cf.investing_cf, "flow"],
    ["From loans and investors", cf.financing_cf, "flow"], ["Cash at the end", cf.cash_ending, "end"]];
  const max = Math.max(1, ...steps.map((s) => Math.abs(s[1] || 0)));
  return `<div class="fin-cash">${steps.map(([l, v, k]) => `
      <div class="fin-cash-row ${k}"><span>${l}</span><div class="fin-cash-track"><i class="${(v || 0) < 0 ? "neg" : "pos"}" style="width:${(100 * Math.abs(v || 0) / max).toFixed(1)}%"></i></div>
        <b class="${k === "flow" ? ((v || 0) < 0 ? "neg" : "pos") : ""}">${k === "flow" && v > 0 ? "+" : ""}${fm(v || 0)}</b></div>`).join("")}</div>
    <p class="chart-caption">Profit isn't cash: customers pay late, equipment is paid up front, and loans bring cash without being income.</p>`;
}

const FIN_RATIOS = [
  ["Profit", [
    ["gross_margin_pct", "Gross margin", "%", (v) => (v >= 40 ? "good" : v >= 20 ? "ok" : "bad"), "What's left of each sale after the cost of making it.", "healthy above 40%"],
    ["operating_margin_pct", "Operating margin", "%", (v) => (v >= 10 ? "good" : v >= 0 ? "ok" : "bad"), "Profit from running the business, before interest and tax.", "healthy above 10%"],
    ["net_margin_pct", "Net margin", "%", (v) => (v >= 8 ? "good" : v >= 0 ? "ok" : "bad"), "What you keep from each dollar of revenue.", "healthy above 8%"]]],
  ["Paying the bills", [
    ["current_ratio", "Current ratio", "x", (v) => (v === null || v >= 1.5 ? "good" : v >= 1 ? "ok" : "bad"), "Short-term assets over short-term bills.", "healthy above 1.5×"],
    ["quick_ratio", "Quick ratio", "x", (v) => (v === null || v >= 1 ? "good" : v >= 0.7 ? "ok" : "bad"), "The same, without counting stock.", "healthy above 1×"]]],
  ["Debt", [
    ["debt_to_equity", "Debt / equity", "x", (v) => (v === null || (v >= 0 && v < 1) ? "good" : v < 2 && v >= 0 ? "ok" : "bad"), "How much of the company lenders paid for.", "healthy below 1×"],
    ["interest_coverage", "Interest cover", "x", (v) => (v === null || v >= 3 ? "good" : v >= 1.5 ? "ok" : "bad"), "Operating profit over interest. Banks rate you on it.", "healthy above 3×"]]],
  ["Returns", [
    ["roe_pct", "Return on equity", "%", (v) => (v >= 15 ? "good" : v >= 0 ? "ok" : "bad"), "Profit for every dollar the owners have in.", "healthy above 15%"],
    ["roa_pct", "Return on assets", "%", (v) => (v >= 5 ? "good" : v >= 0 ? "ok" : "bad"), "Profit for every dollar of things you own.", "healthy above 5%"]]],
];

function finRatiosHtml(r) {
  const ccc = r.cash_conversion_cycle || {}, du = r.dupont || {};
  return `${FIN_RATIOS.map(([group, items]) => `<section class="fin-rgroup"><h4>${group}</h4><div class="fin-rcards">${items.map(([k, name, unit, rate, what, rule]) => {
    const v = r[k], st = v === null || v === undefined ? "good" : rate(v);
    return `<div class="fin-rcard ${st}"><span>${name}</span><b>${unit === "%" ? pctTxt(v) : v === null || v === undefined ? "None" : xTxt(v)}</b><p>${what}</p><em>${rule}</em></div>`;
  }).join("")}</div></section>`).join("")}
    <section class="fin-rgroup"><h4>How fast cash comes back</h4><div class="fin-cycle">
      <span>Stock sits <b>${Math.round(ccc.dio_days || 0)}</b> days</span><span>+ customers pay in <b>${Math.round(ccc.dso_days || 0)}</b> days</span>
      <span>− you pay suppliers in <b>${Math.round(ccc.dpo_days || 0)}</b> days</span><span class="fin-cyc-total">= cash tied up <b>${Math.round(ccc.ccc_days || 0)}</b> days</span></div></section>
    <section class="fin-rgroup"><h4>Return on equity, taken apart</h4><div class="fin-cycle">
      <span>Margin <b>${pctTxt(du.net_margin_pct)}</b></span><span>× asset turnover <b>${xTxt(du.asset_turnover)}</b></span><span>× leverage <b>${xTxt(du.equity_multiplier)}</b></span>
      <span class="fin-cyc-total">= ROE <b>${pctTxt(du.roe_check_pct)}</b></span></div></section>`;
}

async function finTrend(host) {
  try {
    const hist = await fetch(`${API}/snapshots`).then((r) => r.json());
    if (!host.isConnected || hist.length < 2) { host.innerHTML = `<p class="chart-caption">Play a few months to see the trend.</p>`; return hist; }
    const last = hist.slice(-24);
    trendChart = mountChart(host, { kind: "line", unit: "$", title: "Revenue, profit and cash by month", x: last.map((s) => `M${s.month}`),
      series: [{ role: "revenue", label: "Revenue", values: last.map((s) => s.income_statement.revenue) },
        { role: "profit", label: "Profit", values: last.map((s) => s.income_statement.net_income) },
        { role: "cash", label: "Cash", values: last.map((s) => s.balance_sheet.CASH) }] }, { flat: true });
    return hist;
  } catch (e) { return []; }
}

async function renderFinancials(main, snap, sub) {
  if (sub) financialsSubTab = sub;
  const is = snap.income_statement, bs = snap.balance_sheet, cf = snap.cash_flow, r = snap.ratios || {};
  const run = finRunway(is, bs);
  main.innerHTML = `
    <div class="fin">
      <div class="fin-head"><h2 class="panel-title">Financials · month ${snap.month} ${helpIcon("financials")}</h2><p class="fin-plain">${finPlain(is, bs)}</p></div>
      <div class="fin-kpis">
        <div><span>Revenue</span><b>${fmc(is.revenue)}</b></div>
        <div><span>${is.net_income >= 0 ? "Profit" : "Loss"}</span><b class="${is.net_income < 0 ? "neg" : "pos"}">${fmc(is.net_income)}</b></div>
        <div><span>Cash</span><b class="${bs.CASH < 0 ? "neg" : ""}">${fmc(bs.CASH)}</b></div>
        <div><span>Runway</span><b>${run === null ? (is.revenue ? "Profitable" : "—") : `${run.toFixed(1)} mo`}</b></div>
        <button type="button" class="fin-val" data-fin-valuation><span>Valuation ⓘ</span><b>${r.valuation_status === "not_valued" ? "Not valued yet" : fmc(r.valuation)}</b></button>
      </div>
      <nav class="fin-tabs" role="tablist">${FIN_TABS.map(([k, l]) => `<button type="button" role="tab" data-fsub="${k}" class="${financialsSubTab === k ? "on" : ""}" aria-selected="${financialsSubTab === k}">${l}</button>`).join("")}</nav>
      <div id="financials-content" class="fin-body"></div>
    </div>`;
  main.querySelectorAll("[data-fsub]").forEach((b) => b.addEventListener("click", () => renderFinancials(main, snap, b.dataset.fsub)));
  const vb = main.querySelector("[data-fin-valuation]");
  if (vb) vb.addEventListener("click", () => window.openValuationExplainer && window.openValuationExplainer());

  if (pnlChart) { pnlChart.dispose(); pnlChart = null; }
  if (trendChart) { trendChart.dispose(); trendChart = null; }
  const content = $("financials-content");
  const t = financialsSubTab;
  if (t === "summary") {
    content.innerHTML = `<div id="fin-health"></div><div class="chart-wrap" style="height:260px;"><div id="fin-trend" class="ck-host"></div></div>`;
    content.querySelector("#fin-health").innerHTML = finHealth(snap, []);
    const hist = await finTrend(content.querySelector("#fin-trend"));
    const h = content.querySelector("#fin-health");
    if (h && hist && hist.length > 1) h.innerHTML = finHealth(snap, hist);
  } else if (t === "pnl") {
    content.innerHTML = finPnlHtml(is);
    pnlChart = mountChart($("pnl-waterfall"), { kind: "waterfall", unit: "$", title: `Where month ${snap.month}'s revenue went`, steps: pnlSteps(is) });
  } else if (t === "balance") {
    content.innerHTML = finBalanceHtml(snap);
  } else if (t === "cashflow") {
    content.innerHTML = finCashHtml(cf);
  } else {
    content.innerHTML = finRatiosHtml(r);
  }
}

function renderRatios(main, snap) {
  return renderFinancials(main, snap, "ratios");
}

export { renderFinancials, renderRatios };
